import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { callEdgeFunction } from "@/lib/edgeFunctions";
import { resolveAvatarUrl } from "@/lib/avatarUrl";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: { display_name: string | null; avatar_url: string | null } | null;
  isPro: boolean;
  proLoading: boolean;
  subscriptionEnd: string | null;
  refreshSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  profile: null,
  isPro: false,
  proLoading: true,
  subscriptionEnd: null,
  refreshSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<{ display_name: string | null; avatar_url: string | null } | null>(null);
  // The app is now 100% free — every feature is unlocked for all users.
  const isPro = true;
  const proLoading = false;
  const subscriptionEnd: string | null = null;
  const setIsPro = (_: boolean) => {};
  const setSubscriptionEnd = (_: string | null) => {};
  const setProLoading = (_: boolean) => {};

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("id", userId)
      .single();
    if (!data) {
      setProfile(null);
      return;
    }
    const displayUrl = await resolveAvatarUrl(data.avatar_url);
    setProfile({ display_name: data.display_name, avatar_url: displayUrl });
  };

  const checkSubscription = useCallback(async () => {
    try {
      const { data, error } = await callEdgeFunction<{ subscribed?: boolean; subscription_end?: string | null }>("check-subscription", { authHeaderName: "x-user-auth" });
      if (error) throw error;
      setIsPro(data?.subscribed === true);
      setSubscriptionEnd(data?.subscription_end ?? null);
    } catch (err) {
      console.warn("Subscription check failed:", err);
      setIsPro(false);
      setSubscriptionEnd(null);
    } finally {
      setProLoading(false);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => fetchProfile(session.user.id), 0);
          setTimeout(() => checkSubscription(), 0);
        } else {
          setProfile(null);
          setIsPro(false);
          setProLoading(false);
          setSubscriptionEnd(null);
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // Stale refresh token — clear local state silently
        console.warn("Session recovery failed, signing out:", error.message);
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setProLoading(false);
        setLoading(false);
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        checkSubscription();
      } else {
        setProLoading(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [checkSubscription]);

  // Auto-refresh subscription every 60s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsPro(false);
    setSubscriptionEnd(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, profile, isPro, proLoading, subscriptionEnd, refreshSubscription: checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
